// Phase 260 — SportContextHeader
//
// Drop-in component that surfaces the active sport's context (surface name,
// primary/secondary metric labels) so coaches viewing a multi-sport academy
// instantly know which sport the dashboard is currently re-skinned for.
//
// Honest deterministic config — driven by /api/sports/registry. No ML, no
// inference. The sport prop is provided explicitly by the parent page based
// on the actual batch / student / filter context.
import { useSportConfig } from '@/hooks/useSportConfig';
import { Skeleton } from '@/components/ui/skeleton';

export function SportContextHeader({ sport, compact = false }) {
  const { config, loading } = useSportConfig(sport);

  if (loading) {
    return <Skeleton className={`${compact ? 'h-9' : 'h-14'} w-full rounded-lg`} data-testid="sport-context-loading" />;
  }

  const accent = config.color_accent || '#0d9488';

  if (compact) {
    return (
      <div
        className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900/40 text-xs"
        data-testid="sport-context-header-compact"
        data-sport-key={config.key}
      >
        <span className="text-base" aria-hidden>{config.emoji}</span>
        <span className="font-bold text-slate-900 dark:text-slate-100">{config.display_name}</span>
        <span className="text-slate-300 dark:text-slate-600">·</span>
        <span className="text-muted-foreground">
          <span className="font-mono font-semibold" style={{ color: accent }}>{config.primary_metric.label}</span>
          <span className="mx-1.5 text-slate-300 dark:text-slate-600">/</span>
          <span className="font-mono font-semibold text-slate-500">{config.secondary_metric.label}</span>
        </span>
      </div>
    );
  }

  return (
    <div
      className="p-3 rounded-2xl border border-slate-200 dark:border-slate-700 bg-gradient-to-r from-slate-50 to-white dark:from-slate-900/40 dark:to-slate-900/20"
      data-testid="sport-context-header"
      data-sport-key={config.key}
    >
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div
            className="h-10 w-10 rounded-full flex items-center justify-center text-2xl"
            style={{ backgroundColor: `${accent}1a`, color: accent }}
            aria-hidden
          >
            {config.emoji}
          </div>
          <div>
            <p className="text-[10px] uppercase font-bold tracking-wider text-slate-400">Active Sport Context</p>
            <p className="text-sm font-bold text-slate-900 dark:text-slate-100">
              {config.display_name}
              <span className="ml-2 text-xs font-mono text-muted-foreground">{config.surface_label}</span>
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 text-xs">
          <div className="px-2.5 py-1 rounded-lg border" style={{ borderColor: `${accent}40`, backgroundColor: `${accent}0a` }}>
            <span className="text-[9px] uppercase font-bold tracking-wider text-slate-400">Primary</span>
            <span className="ml-1.5 font-mono font-bold" style={{ color: accent }} data-testid="sport-primary-label">{config.primary_metric.label}</span>
          </div>
          <div className="px-2.5 py-1 rounded-lg border border-slate-200 dark:border-slate-700">
            <span className="text-[9px] uppercase font-bold tracking-wider text-slate-400">Secondary</span>
            <span className="ml-1.5 font-mono font-bold text-slate-700 dark:text-slate-300" data-testid="sport-secondary-label">{config.secondary_metric.label}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
